"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { trackEvent } from "@/utils/analytics";

interface ReportData {
  id: string;
  title: string;
  url: string;
}

const REPORTS: ReportData[] = [
  {
    id: "hiring-by-stage-playbook",
    title: "투자 시리즈별 채용 플레이북",
    url: "https://featpaper.com/l/zsboaulQ",
  },
  {
    id: "offer-acceptance-playbook",
    title: "오퍼 수락률 향상 플레이북",
    url: "https://featpaper.com/l/Yysdqf9L",
  },
  {
    id: "engineering-manager-hiring",
    title: "엔지니어링 매니저 채용 가이드",
    url: "https://featpaper.com/l/RDyihofM",
  },
  {
    id: "employer-brand-scorecard",
    title: "채용 브랜딩 스코어카드",
    url: "https://featpaper.com/l/noOi7h6C",
  },
  {
    id: "jd-conversion-benchmark",
    title: "JD 채용 전환율 벤치마크",
    url: "https://featpaper.com/l/uNVXuSl0",
  },
  {
    id: "hiring-roi-calculator",
    title: "채용 ROI 계산기",
    url: "https://featpaper.com/l/KcGqpkFb",
  },
  {
    id: "direct-sourcing-benchmark",
    title: "다이렉트 소싱 벤치마크 2026",
    url: "https://featpaper.com/l/oguLRdrU",
  },
];

function ReportsSection() {
  const path = usePathname();

  const impressionEvents = useMemo(
    () =>
      REPORTS.map((report, idx) => ({
        eventName: "report_impression",
        params: {
          report_id: report.id,
          cta_position: "home_reports",
          card_position: idx + 1,
        },
      })),
    [],
  );

  const sectionRef =
    useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleClick = (reportId: string, position: number) => {
    trackEvent("report_outbound_click", path, {
      report_id: reportId,
      cta_position: "home_reports",
      card_position: position,
    });
  };

  return (
    <section className="mt-20 bg-white px-side py-16 tablet:py-20">
      <div
        ref={sectionRef}
        className="mx-auto flex max-w-[1200px] flex-col items-center"
      >
        {/* 섹션 헤더 */}
        <div className="mb-10 text-center tablet:mb-14">
          <div className="mb-2 text-16 font-semibold text-primary tablet:text-18 desktop:text-20">
            채용 자료실
          </div>
          <h2 className="break-keep text-22 font-semibold leading-[1.35] tablet:text-26 desktop:text-30">
            서치라이트AI 무료 리포트
          </h2>
          <p className="mt-3 break-keep text-16 leading-[1.65] text-gray tablet:text-18">
            현장에서 검증한 채용 인사이트와 채용 리더가 자주 묻는 질문들을
            정리했어요
          </p>
        </div>

        {/* 버튼 그리드 — 모바일 1열 / 태블릿 2열 / 데스크톱 4열 */}
        <div className="grid w-full grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-4 desktop:gap-4">
          {REPORTS.map((report, idx) => (
            <a
              key={report.id}
              href={report.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleClick(report.id, idx + 1)}
              className="group flex items-center justify-between gap-4 rounded-xl bg-lightgray/40 px-5 py-5 transition hover:bg-lightgray/70 hover:shadow-sm"
            >
              <span className="break-keep text-15 font-semibold leading-[1.4] text-black tablet:text-16">
                {report.title}
              </span>
              <svg
                aria-hidden="true"
                className="flex-shrink-0 text-gray transition group-hover:text-primary"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>다운로드</title>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ReportsSection;
